import SetCredentialsDialog from '@components/Activation/SetCredentialsDialog';
import LicenseFilesCard from '@components/Licenses/LicenseFilesCard';
import OfflineCreditDialog from '@components/Licenses/OfflineCreditDialog';
import SeatTimeDialog from '@components/Licenses/SeatTimeDialog';
import ConfirmDialog from '@components/Shared/ConfirmDialog';
import Header from '@components/Shared/Layout/Header';
import { Loading, LoadFailed } from '@components/Shared/LoadState';
import SeatUsageBar from '@components/Shared/SeatUsageBar';
import { expiresInText, expiryReminderSeverity } from '@constants/expiry';
import { ApplicationFeatureNames } from '@constants/features';
import { ROUTE_PATHS } from '@constants/routes';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  Alert, Box, Button, Card, CardContent, Chip, IconButton, Link, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import {
  useGetLicensesQuery, useUpdateLicenseMutation, type License, type OfflineCreditLowered, type SeatTimeRaised
} from '@store/api/licenseEndpoints';
import { useState } from 'react';
import { generatePath, Link as RouterLink } from 'react-router-dom';

/** Seats actually held right now, both families — what locking cuts off. */
const heldSeats = (license: License) => license.usedDeviceCount + license.usedComposerDeviceCount;

const seatUsagePath = (license: License) => generatePath(ROUTE_PATHS.SEAT_USAGE, { licenseId: license.licenseId });

/** The licence file allows some offline credit: zero in either of its values means none. */
const fileAllowsCredit = (license: License) =>
  (license.offlineCreditWindowSeconds ?? 0) > 0 && (license.offlineCreditBudgetSeconds ?? 0) > 0;

/** The administrator has set values of their own, which the dialog can change or clear. */
const ownCredit = (license: License) =>
  license.offlineCreditAdminWindowSeconds != null || license.offlineCreditAdminBudgetSeconds != null;

/**
 * The offline credit this server issues for the licence: the lower of the administrator's value
 * and the licence file's. Zero in either value means none.
 */
const offlineCreditText = (license: License) => {
  const window = license.effectiveOfflineCreditWindowSeconds;
  const budget = license.effectiveOfflineCreditBudgetSeconds;

  if (window == null || budget == null) return 'Offline credit: not set yet (applied at the next server start)';

  // Nothing set here can raise the file's values, so say why rather than offer a change.
  if (!fileAllowsCredit(license)) return 'Offline credit off: the license file allows none';

  const issued = window === 0 || budget === 0
    ? 'Offline credit off'
    : `Offline credit: ${budget.formatDuration()} of use within ${window.formatDuration()} of the last seat`;

  const own = ownCredit(license);

  return own
    ? `${issued} (set here; the license file allows ${(license.offlineCreditBudgetSeconds ?? 0).formatDuration()}` +
      ` within ${(license.offlineCreditWindowSeconds ?? 0).formatDuration()})`
    : issued;
};

/**
 * The seat time seats on the licence are given, and where it comes from: the administrator's own
 * when one is set, the licence file's otherwise. The server works it out; the page only says it.
 */
const seatTimeText = (license: License) => {
  const given = (license.effectiveSeatAllocationTimeInSeconds ?? license.seatAllocationTimeInSeconds).formatDuration();

  return license.seatAllocationAdminTimeInSeconds != null
    ? `Seat time: ${given}, set here (the license file's is ${license.seatAllocationTimeInSeconds.formatDuration()})`
    : `Seat time: ${given}, from the license file`;
};

/** What a licence file did to the administrator's seat time, for the notice on the card. */
const raisedText = (raised: SeatTimeRaised) =>
  `Seat time raised by license file ${raised.fileId.slice(0, 8)} on ${new Date(raised.at).toFormattedString()}: ` +
  `${raised.from.formatDuration()} to ${raised.to.formatDuration()}. That file sets no less.`;

/** What a licence file did to the administrator's values, for the notice on the card. */
const loweredText = (lowered: OfflineCreditLowered) => {
  const parts = [
    lowered.windowSeconds &&
      `window ${lowered.windowSeconds.from.formatDuration()} to ${lowered.windowSeconds.to.formatDuration()}`,
    lowered.budgetSeconds &&
      `budget ${lowered.budgetSeconds.from.formatDuration()} to ${lowered.budgetSeconds.to.formatDuration()}`
  ].filter(Boolean);

  return `Offline credit lowered by license file ${lowered.fileId.slice(0, 8)} on ` +
    `${new Date(lowered.at).toFormattedString()}: ${parts.join(', ')}. That file allows no more.`;
};

const LicensesPage = () => {
  const { data: licenses = [], isLoading, isError, refetch } = useGetLicensesQuery();
  const [updateLicense] = useUpdateLicenseMutation();

  const [credentialsTarget, setCredentialsTarget] = useState<License>();
  const [creditTarget, setCreditTarget] = useState<License>();
  const [seatTimeTarget, setSeatTimeTarget] = useState<License>();
  const [editingLabel, setEditingLabel] = useState<{ licenseId: string, value: string }>();
  // Only locking is confirmed. Unlocking restores service, and a dialog in front of the fix for
  // an outage is a dialog somebody reads while the site is down.
  const [lockTarget, setLockTarget] = useState<License>();

  const saveLabel = async () => {
    if (!editingLabel) return;

    await updateLicense({ licenseId: editingLabel.licenseId, label: editingLabel.value });
    setEditingLabel(undefined);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Licenses'
        description={'Seat counts, features and expiry come from the signed license file. ' +
          'The credentials, the name and the lock are set here.'}
      />

      <LicenseFilesCard />

      {/* Only a list that loaded says there are none: while it loads, or when it could not be
          loaded, "no licenses" would be a statement about the server the page cannot make. */}
      {isLoading ? (
        <Card><CardContent><Loading text='Loading the licenses…' /></CardContent></Card>
      ) : isError ? (
        <LoadFailed text='The licenses could not be loaded.' onRetry={() => { void refetch(); }} />
      ) : licenses.length === 0 ? (
        <Card><CardContent>
          <Typography variant='body2' color='text.secondary'>
            No licenses installed yet. Import the license file CDMVision issued for this server.
          </Typography>
        </CardContent></Card>
      ) : licenses.map(license => (
        <Card key={license.licenseId} sx={{ mb: 2 }}>
          <CardContent>
            {/* On a narrow screen the seat and action columns go under the details: beside them,
                their fixed widths would leave the details a column a word wide. */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              useFlexGap
              alignItems='flex-start'
              justifyContent='space-between'
            >
              {/* minWidth: 0 — without it a long activation id or a full row of feature
                  chips would refuse to shrink and shove the fixed columns out of line. */}
              <Box sx={{ flexGrow: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
                <Stack direction='row' spacing={1} useFlexGap alignItems='center' sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant='h6'><code>{license.id ?? 'no activation id'}</code></Typography>

                  {license.locked && <Chip size='small' color='error' icon={<LockIcon />} label='Locked' />}
                  {(!license.id || !license.passwordSet) &&
                    <Chip size='small' color='warning' label='Credentials required' />}
                  {/* Both on the server's clock, like the certificate's reminders. */}
                  {license.expiresInDays <= 0 &&
                    <Chip size='small' color='error' label='Expired' />}
                  {license.expiryReminderDays !== undefined &&
                    <Chip
                      size='small'
                      color={expiryReminderSeverity(license.expiryReminderDays)}
                      label={`Expires ${expiresInText(license.expiresInDays)}`}
                    />}
                  {/* From the seat usage history, decided by the server: the licence to look at. */}
                  {(license.seatUsage30d.atLimit || license.seatUsage30d.exceeded) &&
                    <Tooltip title='On at least one day of the last 30, in UTC. Open its seat usage for the days.'>
                      <Chip
                        size='small'
                        clickable
                        component={RouterLink}
                        to={seatUsagePath(license)}
                        color={license.seatUsage30d.exceeded ? 'error' : 'warning'}
                        label={license.seatUsage30d.exceeded
                          ? 'Seat limit exceeded (last 30 days)'
                          : 'Seat limit reached (last 30 days)'}
                      />
                    </Tooltip>}
                </Stack>

                {editingLabel?.licenseId === license.licenseId ? (
                  <Stack direction='row' spacing={1} sx={{ mb: 1, maxWidth: 420 }}>
                    <TextField
                      size='small'
                      value={editingLabel.value}
                      onChange={(event) => setEditingLabel({ ...editingLabel, value: event.target.value })}
                      fullWidth
                      autoFocus
                    />
                    <Button size='small' variant='contained' onClick={saveLabel}>Save</Button>
                    <Button size='small' onClick={() => setEditingLabel(undefined)}>Cancel</Button>
                  </Stack>
                ) : (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1, cursor: 'pointer' }}
                    onClick={() => setEditingLabel({ licenseId: license.licenseId, value: license.label ?? '' })}
                  >
                    {license.label || 'Add a label…'}
                  </Typography>
                )}

                <Typography variant='caption' color='text.secondary' display='block'>
                  Expires {new Date(license.expirationDate).toFormattedString()} ·
                  {' '}issued {new Date(license.issueDate).toFormattedString()} ·
                  {' '}file <code>{license.sourceFileId.slice(0, 8)}</code>
                </Typography>

                <Typography variant='caption' color='text.secondary' display='block'>
                  <Tooltip title='How long a seat stays with a device that has stopped calling back, before another device may take it.'>
                    <span>{seatTimeText(license)}</span>
                  </Tooltip>
                  {/* Floating licences only, as the server allows, and there always: a seat time
                      may be anything from the file's to a week, and no file's is above a week. */}
                  {license.isFloating && (
                    <>
                      {' · '}
                      <Link component='button' variant='caption' onClick={() => setSeatTimeTarget(license)}>
                        Change
                      </Link>
                    </>
                  )}
                </Typography>

                {license.seatAllocationTimeRaised && (
                  <Alert severity='warning' sx={{ mt: 1, py: 0 }}>
                    {raisedText(license.seatAllocationTimeRaised)}
                  </Alert>
                )}

                <Typography variant='caption' color='text.secondary' display='block'>
                  {offlineCreditText(license)}
                  {/* Only where the dialog can change something: the file allows some credit, or
                      there are values of the administrator's own to change or clear. */}
                  {license.isFloating && license.offlineCreditWindowSeconds != null &&
                    (fileAllowsCredit(license) || ownCredit(license)) && (
                    <>
                      {' · '}
                      <Link component='button' variant='caption' onClick={() => setCreditTarget(license)}>
                        Change
                      </Link>
                    </>
                  )}
                </Typography>

                {license.offlineCreditLowered && (
                  <Alert severity='warning' sx={{ mt: 1, py: 0 }}>
                    {loweredText(license.offlineCreditLowered)}
                  </Alert>
                )}

                <Stack direction='row' spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {license.applicationFeatures.length === 0 && (
                    <Typography variant='caption' color='text.secondary'>No features</Typography>
                  )}
                  {license.applicationFeatures.map(feature => (
                    <Chip
                      key={feature}
                      size='small'
                      variant='outlined'
                      label={ApplicationFeatureNames[feature] ?? feature.slice(0, 8)}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Fixed width and no shrinking: the seat column has to start at the same x
                  on every card, otherwise the action column after it walks left and right. */}
              <Stack spacing={1.5} sx={{ width: { xs: '100%', md: 190 }, maxWidth: { xs: 360, md: 'none' }, flexShrink: 0, pt: 0.5 }}>
                <SeatUsageBar
                  used={license.usedDeviceCount}
                  reclaimable={license.reclaimableDeviceCount}
                  limit={license.deviceLimitCount}
                  label='SuPAR'
                />
                <SeatUsageBar
                  used={license.usedComposerDeviceCount}
                  reclaimable={license.reclaimableComposerDeviceCount}
                  limit={license.composerDeviceLimitCount}
                  label='Composer'
                />
              </Stack>

              <Stack
                direction={{ xs: 'row', md: 'column' }}
                spacing={1}
                useFlexGap
                alignItems={{ xs: 'center', md: 'flex-end' }}
                sx={{ width: { xs: '100%', md: 170 }, flexShrink: 0, flexWrap: 'wrap' }}
              >
                <Button size='small' onClick={() => setCredentialsTarget(license)}>
                  {license.id && license.passwordSet ? 'Change credentials' : 'Set credentials'}
                </Button>

                <Button size='small' component={RouterLink} to={seatUsagePath(license)}>Seat usage</Button>

                <Tooltip title={license.locked ? 'Unlock this license' : 'Lock this license'}>
                  <IconButton
                    size='small'
                    aria-label={license.locked ? 'Unlock license' : 'Lock license'}
                    onClick={() => license.locked
                      ? updateLicense({ licenseId: license.licenseId, locked: false })
                      : setLockTarget(license)}
                  >
                    {license.locked ? <LockIcon fontSize='small' /> : <LockOpenIcon fontSize='small' />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <SetCredentialsDialog license={credentialsTarget} onClose={() => setCredentialsTarget(undefined)} />

      <OfflineCreditDialog license={creditTarget} onClose={() => setCreditTarget(undefined)} />

      <SeatTimeDialog license={seatTimeTarget} onClose={() => setSeatTimeTarget(undefined)} />

      {lockTarget && (
        <ConfirmDialog
          title='Lock this license?'
          confirmLabel='Lock license'
          confirmColor='warning'
          onClose={() => setLockTarget(undefined)}
          onConfirm={() => updateLicense({ licenseId: lockTarget.licenseId, locked: true })}
        >
          Every client of <code>{lockTarget.id ?? lockTarget.label ?? 'this license'}</code> is
          refused while it is locked, not only new ones. {heldSeats(lockTarget) === 0
            ? 'No device holds a seat on it at the moment.'
            : `The ${heldSeats(lockTarget)} device${heldSeats(lockTarget) === 1 ? '' : 's'} holding
               a seat now ${heldSeats(lockTarget) === 1 ? 'is' : 'are'} cut off at the next
               heartbeat, within fifteen minutes.`}
          {' '}Nothing is deleted and no seat is released: unlocking puts it back as it was.
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default LicensesPage;
